import { formatLocalizedDate } from "./dateUtils";
import { salonInfo } from "../data/salonInfo";
import { services as defaultServices } from "../data/services";
import { galleryItems as defaultGalleryItems } from "../data/gallery";

const STORAGE_KEY = "aurahair_rotterdam_appointments_v1";
const USERS_KEY = "aurahair_rotterdam_users_v1";
const CURRENT_USER_KEY = "aurahair_current_user_session";
const PROMO_CODES_KEY = "aurahair_promo_codes_v1";
const BLOCKED_SLOTS_KEY = "aurahair_blocked_slots_v1";
const GIFT_CARDS_KEY = "aurahair_gift_cards_v1";

// Broadcast Channel for same-device multi-tab instant sync
let syncChannel = null;
try {
  if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
    syncChannel = new BroadcastChannel('aurahair_sync_channel');
  }
} catch (e) {}

export function broadcastDataChange(type, data) {
  if (syncChannel) {
    try {
      syncChannel.postMessage({ type, data, timestamp: Date.now() });
    } catch (e) {}
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('aurahair_data_change', { detail: { type, data } }));
  }
}

// Initial realistic gift cards
const INITIAL_GIFT_CARDS = [
  {
    id: "AURA-GIFT-9482-GOLD",
    code: "AURA-GIFT-9482-GOLD",
    amount: 50,
    theme: "gold",
    senderName: "Daan van Dijk",
    recipientName: "Jan van Dijk",
    message: "Fijne 60e verjaardag Pa! Geniet van een luxe behandeling bij Aura Hair.",
    createdAt: new Date(Date.now() - 3600000 * 24 * 5).toISOString(),
    expiresAt: new Date(Date.now() + 3600000 * 24 * 360).toISOString(),
    isUsed: false,
    usedAt: null
  },
  {
    id: "AURA-GIFT-7319-VIP",
    code: "AURA-GIFT-7319-VIP",
    amount: 75,
    theme: "emerald",
    senderName: "Emma",
    recipientName: "Sander de Boer",
    message: "Veel plezier met je nieuwe look bij de beste kapper van Rotterdam!",
    createdAt: new Date(Date.now() - 3600000 * 24 * 2).toISOString(),
    expiresAt: new Date(Date.now() + 3600000 * 24 * 363).toISOString(),
    isUsed: false,
    usedAt: null
  }
];

// Initial realistic users with existing loyalty stamps (hashed passwords)
const INITIAL_USERS = [
  {
    id: "usr-daan",
    name: "Daan van Dijk",
    phone: "+31 6 12 34 56 78",
    email: "daan.vandijk@example.nl",
    passwordHash: "a6c923608adfb9a161b2dd8c8e0066eeebf47d12c39ddf8fcb6b1c744151cdce",
    completedAppointments: 9,
    createdAt: new Date(Date.now() - 3600000 * 24 * 90).toISOString()
  },
  {
    id: "usr-lars",
    name: "Lars Bakker",
    phone: "+31 6 98 76 54 32",
    email: "lars@example.nl",
    passwordHash: "a6c923608adfb9a161b2dd8c8e0066eeebf47d12c39ddf8fcb6b1c744151cdce",
    completedAppointments: 3,
    createdAt: new Date(Date.now() - 3600000 * 24 * 30).toISOString()
  }
];

// Initial Promo Codes
const INITIAL_PROMO_CODES = [
  { id: "promo-1", code: "AURA2026", type: "percent", value: 10, description: "Aura Hair %10 Açılış İndirimi" },
  { id: "promo-2", code: "ROTTERDAM", type: "fixed", value: 5, description: "Rotterdam €5 Hoş Geldin İndirimi" },
  { id: "promo-3", code: "VIPAURA", type: "percent", value: 15, description: "Özel VIP %15 İndirim Kuponu" },
  { id: "promo-gift-1", code: "AURA-GIFT-9482-GOLD", type: "fixed", value: 50, description: "Hediye Çeki (€50) - Daan van Dijk" },
  { id: "promo-gift-2", code: "AURA-GIFT-7319-VIP", type: "fixed", value: 75, description: "Hediye Çeki (€75) - Emma" }
];

// Initial realistic appointments for Aura Hair Rotterdam
const INITIAL_APPOINTMENTS = [];

// ==================== REAL-TIME SERVER API SYNC ====================

export function getApiBaseUrl() {
  if (typeof window === 'undefined') return 'https://rotterdamaurahair.com';
  // Local Vite dev server
  if (window.location.hostname === 'localhost' && window.location.port === '5173') {
    return '';
  }
  // Android / iOS Capacitor Native Webview (localhost on default port or capacitor: / file:)
  if (
    window.location.protocol === 'capacitor:' ||
    window.location.protocol === 'file:' ||
    (window.location.hostname === 'localhost' && window.location.port !== '5173') ||
    (typeof window.Capacitor !== 'undefined' && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform())
  ) {
    return 'https://rotterdamaurahair.com';
  }
  return '';
}

export async function fetchAppointmentsFromServer() {
  if (typeof window === 'undefined') return getStoredAppointments();
  try {
    const apiUrl = `${getApiBaseUrl()}/api/appointments`;
    const res = await fetch(apiUrl);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data)) {
        const normalized = data.map(a => ({
          ...a,
          phone: a.phone || a.customerPhone || '',
          customerPhone: a.customerPhone || a.phone || '',
          email: a.email || a.customerEmail || '',
          customerEmail: a.customerEmail || a.email || '',
          barber: a.barber || a.barberName || 'Aura Barber',
          barberName: a.barberName || a.barber || 'Aura Barber',
          customerName: a.customerName || a.name || 'Misafir'
        }));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
        broadcastDataChange('APPOINTMENTS_SYNCED', normalized);
        return normalized;
      }
    }
  } catch (e) {
    // offline or local fallback
  }
  return getStoredAppointments();
}

export async function fetchCustomersFromServer() {
  if (typeof window === 'undefined') return getAllCustomersWithLoyalty();
  try {
    const apiUrl = `${getApiBaseUrl()}/api/users`;
    const res = await fetch(apiUrl);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data)) {
        localStorage.setItem(USERS_KEY, JSON.stringify(data));
        return getAllCustomersWithLoyalty();
      }
    }
  } catch (e) {}
  return getAllCustomersWithLoyalty();
}

// Initial background prefetch deferred until browser is idle to avoid critical request chains
if (typeof window !== 'undefined') {
  const schedulePrefetch = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        fetchAppointmentsFromServer().catch(() => {});
        fetchSpecialDiscountsFromServer().catch(() => {});
      }, { timeout: 3500 });
    } else {
      setTimeout(() => {
        fetchAppointmentsFromServer().catch(() => {});
        fetchSpecialDiscountsFromServer().catch(() => {});
      }, 3500);
    }
  };

  if (document.readyState === 'complete') {
    schedulePrefetch();
  } else {
    window.addEventListener('load', schedulePrefetch, { once: true });
  }
}

// ==================== PROMO CODES ENGINE ====================

export function getPromoCodes() {
  try {
    const data = localStorage.getItem(PROMO_CODES_KEY);
    if (!data) {
      localStorage.setItem(PROMO_CODES_KEY, JSON.stringify(INITIAL_PROMO_CODES));
      return INITIAL_PROMO_CODES;
    }
    return JSON.parse(data);
  } catch (e) {
    return INITIAL_PROMO_CODES;
  }
}

export function validatePromoCode(rawCode, rawTotalPrice) {
  if (!rawCode) return { valid: false, error: "EMPTY" };
  const cleanCode = rawCode.trim().toUpperCase();
  const codes = getPromoCodes();
  const found = codes.find(c => c.code.toUpperCase() === cleanCode);

  if (!found) {
    // Also check if code matches an active Special Day Discount
    const specials = getSpecialDiscounts();
    const foundSpecial = specials.find(s => s.is_active && s.code && s.code.toUpperCase() === cleanCode);
    if (foundSpecial) {
      const discountType = foundSpecial.discount_type || 'percent';
      const discountVal = Number(foundSpecial.discount_value) || 10;
      let discountAmount = 0;
      if (discountType === "percent") {
        discountAmount = Math.round((rawTotalPrice * discountVal) / 100);
      } else {
        discountAmount = Math.min(rawTotalPrice, discountVal);
      }
      return {
        valid: true,
        promo: {
          id: foundSpecial.id,
          code: foundSpecial.code,
          type: discountType,
          value: discountVal,
          description: foundSpecial.description || foundSpecial.name,
          badge: foundSpecial.badge,
          isSpecialDay: true
        },
        discountAmount,
        discountedPrice: Math.max(0, rawTotalPrice - discountAmount)
      };
    }
    return { valid: false, error: "INVALID" };
  }

  let discountAmount = 0;
  if (found.type === "percent") {
    discountAmount = Math.round((rawTotalPrice * found.value) / 100);
  } else if (found.type === "fixed") {
    discountAmount = Math.min(rawTotalPrice, found.value);
  }

  return {
    valid: true,
    promo: found,
    discountAmount,
    discountedPrice: Math.max(0, rawTotalPrice - discountAmount)
  };
}

export function addPromoCode(newPromo) {
  const current = getPromoCodes();
  const codeObj = {
    id: `promo-${Date.now().toString(36)}`,
    code: newPromo.code.trim().toUpperCase(),
    type: newPromo.type || 'percent',
    value: Number(newPromo.value) || 10,
    description: newPromo.description || ''
  };
  const updated = [codeObj, ...current];
  localStorage.setItem(PROMO_CODES_KEY, JSON.stringify(updated));
  
  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/promo-codes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(codeObj)
    }).catch(() => {});
  }
  
  broadcastDataChange('PROMO_ADDED', codeObj);
  return updated;
}

export function deletePromoCode(promoId) {
  const current = getPromoCodes();
  const updated = current.filter(p => p.id !== promoId);
  localStorage.setItem(PROMO_CODES_KEY, JSON.stringify(updated));
  
  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/promo-codes/${promoId}`, { method: 'DELETE' }).catch(() => {});
  }
  
  broadcastDataChange('PROMO_DELETED', promoId);
  return updated;
}

// ==================== SPECIAL DAY DISCOUNTS ENGINE (Özel Günler İndirimleri) ====================

const SPECIAL_DISCOUNTS_KEY = "aurahair_special_discounts_v2";

const INITIAL_SPECIAL_DISCOUNTS = [
  { id: 'special-valentijn', name: '14 Şubat Sevgililer Günü', name_nl: 'Valentijnsdag Special (14 Feb)', code: 'VALENTINE10', discount_type: 'percent', discount_value: 10, start_date: '2026-02-12', end_date: '2026-02-14', badge: '❤️ 14 Şubat Sevgililer Günü', description: '14 Şubat Sevgililer Gününe özel tüm hizmetlerde %10 indirim', description_nl: '10% Valentijnsdag korting op alle behandelingen', is_active: 1 },
  { id: 'special-kadinlar', name: 'Dünya Kadınlar Günü', name_nl: "Internationale Vrouwendag", code: 'WOMEN10', discount_type: 'percent', discount_value: 10, start_date: '2026-03-06', end_date: '2026-03-08', badge: '🌷 Kadınlar Günü', description: 'Dünya Kadınlar Gününe özel tüm hizmetlerde %10 indirim', description_nl: 'Vier Internationale Vrouwendag met 10% korting op alle behandelingen', is_active: 1 },
  { id: 'special-anneler', name: 'Anneler Günü', name_nl: "Moederdag Special", code: 'MOTHER10', discount_type: 'percent', discount_value: 10, start_date: '2026-05-08', end_date: '2026-05-10', badge: '💐 Anneler Günü', description: 'Anneler gününe özel tüm saç ve güzellik hizmetlerinde %10 indirim', description_nl: 'Verwen je moeder met 10% Moederdag korting bij Aura Hair', is_active: 1 },
  { id: 'special-babalar', name: 'Babalar Günü', name_nl: "Vaderdag Special", code: 'FATHER10', discount_type: 'percent', discount_value: 10, start_date: '2026-06-19', end_date: '2026-06-21', badge: '👨 Babalar Günü', description: 'Babalar gününe özel tüm saç ve sakal hizmetlerinde %10 indirim', description_nl: '10% Vaderdag korting op haar- en baardverzorging bij Aura Hair', active: 1, is_active: 1 }
];

export function getSpecialDiscounts() {
  try {
    const data = localStorage.getItem(SPECIAL_DISCOUNTS_KEY);
    if (!data) {
      localStorage.setItem(SPECIAL_DISCOUNTS_KEY, JSON.stringify(INITIAL_SPECIAL_DISCOUNTS));
      return INITIAL_SPECIAL_DISCOUNTS;
    }
    const parsed = JSON.parse(data);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      localStorage.setItem(SPECIAL_DISCOUNTS_KEY, JSON.stringify(INITIAL_SPECIAL_DISCOUNTS));
      return INITIAL_SPECIAL_DISCOUNTS;
    }
    return parsed;
  } catch (e) {
    return INITIAL_SPECIAL_DISCOUNTS;
  }
}

export function getUpcomingSpecialCampaign(fromDateStr = null) {
  const all = getSpecialDiscounts().filter(item => item.is_active);
  const today = fromDateStr || new Date().toISOString().split('T')[0];
  
  // Find current active
  const activeNow = all.find(item => {
    if (item.start_date && item.end_date) {
      return today >= item.start_date && today <= item.end_date;
    }
    return false;
  });
  if (activeNow) return { ...activeNow, status: 'active', daysLeft: 0 };

  // Find next upcoming
  const futureCampaigns = all
    .filter(item => item.start_date && item.start_date > today)
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  if (futureCampaigns.length > 0) {
    const next = futureCampaigns[0];
    const diffDays = Math.ceil((new Date(next.start_date) - new Date(today)) / (1000 * 60 * 60 * 24));
    return { ...next, status: 'upcoming', daysUntil: diffDays };
  }

  return null;
}

export function getActiveSpecialDiscounts(targetDate = null) {
  const all = getSpecialDiscounts();
  const dateStr = targetDate || new Date().toISOString().split('T')[0];
  
  return all.filter(item => {
    if (!item.is_active) return false;
    if (!item.start_date && !item.end_date) return true;
    if (item.start_date && item.end_date) {
      return dateStr >= item.start_date && dateStr <= item.end_date;
    }
    if (item.start_date) return dateStr >= item.start_date;
    if (item.end_date) return dateStr <= item.end_date;
    return true;
  });
}

export function saveSpecialDiscount(item) {
  const all = getSpecialDiscounts();
  const id = item.id || 'special-' + Date.now().toString(36);
  const updatedItem = {
    ...item,
    id,
    code: (item.code || '').toUpperCase().trim(),
    discount_value: Number(item.discount_value) || 10,
    is_active: item.is_active !== undefined ? (item.is_active ? 1 : 0) : 1
  };
  
  const existingIdx = all.findIndex(d => d.id === id);
  let updated;
  if (existingIdx >= 0) {
    updated = [...all];
    updated[existingIdx] = updatedItem;
  } else {
    updated = [updatedItem, ...all];
  }
  
  localStorage.setItem(SPECIAL_DISCOUNTS_KEY, JSON.stringify(updated));
  
  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/special-discounts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedItem)
    }).catch(() => {});
  }
  
  broadcastDataChange('SPECIAL_DISCOUNT_SAVED', updatedItem);
  return updated;
}

export function toggleSpecialDiscount(id, isActive) {
  const all = getSpecialDiscounts();
  const updated = all.map(d => d.id === id ? { ...d, is_active: isActive ? 1 : 0 } : d);
  localStorage.setItem(SPECIAL_DISCOUNTS_KEY, JSON.stringify(updated));
  
  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/special-discounts/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, isActive: isActive ? 1 : 0 })
    }).catch(() => {});
  }
  
  broadcastDataChange('SPECIAL_DISCOUNT_TOGGLED', { id, isActive });
  return updated;
}

export function deleteSpecialDiscount(id) {
  const all = getSpecialDiscounts();
  const updated = all.filter(d => d.id !== id);
  localStorage.setItem(SPECIAL_DISCOUNTS_KEY, JSON.stringify(updated));
  
  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/special-discounts/${id}`, { method: 'DELETE' }).catch(() => {});
  }
  
  broadcastDataChange('SPECIAL_DISCOUNT_DELETED', id);
  return updated;
}

export async function fetchSpecialDiscountsFromServer() {
  if (typeof fetch === 'undefined') return getSpecialDiscounts();
  try {
    const res = await fetch(`${getApiBaseUrl()}/api/special-discounts`);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        localStorage.setItem(SPECIAL_DISCOUNTS_KEY, JSON.stringify(data));
        broadcastDataChange('SPECIAL_DISCOUNTS_SYNCED', data);
        return data;
      }
    }
  } catch (err) {
    console.warn('Special discounts sync warning:', err);
  }
  return getSpecialDiscounts();
}

// ==================== GIFT CARDS ENGINE ====================

export function getStoredGiftCards() {
  try {
    const data = localStorage.getItem(GIFT_CARDS_KEY);
    if (!data) {
      localStorage.setItem(GIFT_CARDS_KEY, JSON.stringify(INITIAL_GIFT_CARDS));
      return INITIAL_GIFT_CARDS;
    }
    return JSON.parse(data);
  } catch (e) {
    return INITIAL_GIFT_CARDS;
  }
}

export function createGiftCard({ amount, theme, senderName, recipientName, message }) {
  const cards = getStoredGiftCards();
  const randomSuffix = Math.floor(1000 + Math.random() * 9000);
  const themeUpper = (theme || 'GOLD').toUpperCase();
  const newCode = `AURA-GIFT-${randomSuffix}-${themeUpper}`;

  const newCard = {
    id: newCode,
    code: newCode,
    amount: Number(amount),
    theme: theme || 'gold',
    senderName: senderName.trim(),
    recipientName: recipientName.trim(),
    message: message ? message.trim() : '',
    createdAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + 3600000 * 24 * 365).toISOString(),
    isUsed: false,
    usedAt: null
  };

  const updated = [newCard, ...cards];
  localStorage.setItem(GIFT_CARDS_KEY, JSON.stringify(updated));

  addPromoCode({
    code: newCode,
    type: 'fixed',
    value: Number(amount),
    description: `Hediye Çeki (€${amount}) - ${recipientName}`
  });

  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/gift-cards`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newCard)
    }).catch(() => {});
  }

  broadcastDataChange('GIFT_CARD_CREATED', newCard);
  return newCard;
}

export function validateGiftCard(rawCode) {
  if (!rawCode) return { valid: false, error: 'EMPTY' };
  const clean = rawCode.trim().toUpperCase();
  const cards = getStoredGiftCards();
  const found = cards.find(c => c.code.toUpperCase() === clean || c.id.toUpperCase() === clean);

  if (!found) {
    return { valid: false, error: 'NOT_FOUND' };
  }

  if (found.isUsed) {
    return { valid: false, error: 'ALREADY_USED', card: found };
  }

  const isExpired = new Date(found.expiresAt) < new Date();
  if (isExpired) {
    return { valid: false, error: 'EXPIRED', card: found };
  }

  return { valid: true, card: found };
}

export function redeemGiftCard(rawCode) {
  const clean = rawCode.trim().toUpperCase();
  const cards = getStoredGiftCards();
  const card = cards.find(c => c.code.toUpperCase() === clean || c.id.toUpperCase() === clean);

  if (!card || card.isUsed) return null;

  const updated = cards.map(c => {
    if (c.code.toUpperCase() === clean || c.id.toUpperCase() === clean) {
      return { ...c, isUsed: true, usedAt: new Date().toISOString() };
    }
    return c;
  });

  localStorage.setItem(GIFT_CARDS_KEY, JSON.stringify(updated));

  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/gift-cards/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: clean })
    }).catch(() => {});
  }

  broadcastDataChange('GIFT_CARD_REDEEMED', clean);
  return updated.find(c => c.code.toUpperCase() === clean || c.id.toUpperCase() === clean);
}

// ==================== BLOCKED SLOTS ====================

export function getBlockedSlots() {
  try {
    const data = localStorage.getItem(BLOCKED_SLOTS_KEY);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    return [];
  }
}

export function isSlotBlocked(dateStr, timeStr) {
  const slots = getBlockedSlots();
  const isManuallyBlocked = slots.some(s => s.date === dateStr && s.time === timeStr);
  if (isManuallyBlocked) return true;

  const appointments = getStoredAppointments();
  const isAlreadyBooked = appointments.some(a => 
    a.date === dateStr && 
    a.time === timeStr && 
    a.status !== 'cancelled'
  );

  return isAlreadyBooked;
}

export function getSlotInfo(dateStr, timeStr) {
  const slots = getBlockedSlots();
  const manual = slots.find(s => s.date === dateStr && s.time === timeStr);
  if (manual) {
    return { isBlocked: true, type: 'manual', label: manual.reason || 'Kapalı / Gesloten' };
  }

  const appointments = getStoredAppointments();
  const booked = appointments.find(a => a.date === dateStr && a.time === timeStr && a.status !== 'cancelled');
  if (booked) {
    return { isBlocked: true, type: 'booked', label: 'Dolu / Bezet', appointment: booked };
  }

  return { isBlocked: false, type: 'available', label: 'Açık / Beschikbaar' };
}

export function toggleBlockSlot(dateStr, timeStr, reason = "Mola / Bezet") {
  const cleanDate = typeof dateStr === 'object' ? (dateStr.dateStr || '') : String(dateStr || '');
  const cleanTime = typeof timeStr === 'object' ? (timeStr.time || '') : String(timeStr || '');
  if (!cleanDate || !cleanTime) return getBlockedSlots();

  const slots = getBlockedSlots();
  const exists = slots.find(s => s.date === cleanDate && s.time === cleanTime);

  let updated;
  if (exists) {
    updated = slots.filter(s => !(s.date === cleanDate && s.time === cleanTime));
  } else {
    const newSlot = {
      id: `blk-${Date.now().toString(36)}`,
      date: cleanDate,
      time: cleanTime,
      reason,
      createdAt: new Date().toISOString()
    };
    updated = [newSlot, ...slots];
  }

  localStorage.setItem(BLOCKED_SLOTS_KEY, JSON.stringify(updated));

  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/blocked-slots/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date: cleanDate, time: cleanTime, reason })
    }).catch(() => {});
  }

  broadcastDataChange('SLOT_TOGGLED', { date: cleanDate, time: cleanTime });
  return updated;
}

// ==================== USER ACCOUNTS & AUTH ====================

export function getStoredUsers() {
  try {
    const data = localStorage.getItem(USERS_KEY);
    if (!data) {
      localStorage.setItem(USERS_KEY, JSON.stringify(INITIAL_USERS));
      return INITIAL_USERS;
    }
    return JSON.parse(data);
  } catch (e) {
    return INITIAL_USERS;
  }
}

// Browser-safe SHA-256 password hashing helper
export async function hashPasswordClient(rawPassword, salt = 'aurahair_rotterdam_2026') {
  if (!rawPassword) return '';
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const enc = new TextEncoder();
      const data = enc.encode(rawPassword + salt);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (e) {}
  }
  // Deterministic fallback if subtle crypto is unavailable
  let hash = 0;
  const str = rawPassword + salt;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return 'sh_' + Math.abs(hash).toString(16);
}

export function normalizePhone(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  // Return last 8 or 9 digits for comparison (handles +31, 0031, leading 0, etc.)
  return digits.length > 8 ? digits.slice(-8) : digits;
}

export function lookupCustomerByPhone(rawPhone) {
  if (!rawPhone) return null;
  const digits = normalizePhone(rawPhone);
  if (!digits || digits.length < 6) return null;

  const users = getStoredUsers();
  const foundUser = users.find(u => {
    const uDigits = normalizePhone(u.phone);
    return Boolean(uDigits && (uDigits === digits || uDigits.endsWith(digits) || digits.endsWith(uDigits)));
  });

  if (foundUser) return foundUser;

  const appointments = getStoredAppointments();
  const matchingAppt = appointments.find(a => {
    const aPhone = a.phone || a.customerPhone || '';
    const aDigits = normalizePhone(aPhone);
    return Boolean(aDigits && (aDigits === digits || aDigits.endsWith(digits) || digits.endsWith(aDigits)));
  });

  if (matchingAppt) {
    const pastCount = appointments.filter(a => {
      const p = normalizePhone(a.phone || a.customerPhone);
      return Boolean(p && (p === digits || p.endsWith(digits) || digits.endsWith(p)));
    }).length;

    return {
      id: matchingAppt.customerId || `usr-${digits}`,
      name: matchingAppt.customerName || matchingAppt.name || '',
      phone: matchingAppt.phone || matchingAppt.customerPhone || rawPhone,
      email: matchingAppt.email || matchingAppt.customerEmail || '',
      completedAppointments: pastCount
    };
  }

  return null;
}

export function registerUser({ name, phone, email, password }) {
  const users = getStoredUsers();
  const cleanPhone = (phone || '').trim();
  const phoneDigits = normalizePhone(cleanPhone);
  const cleanEmail = (email || '').trim().toLowerCase();

  // 1. Check existing registered users
  const existing = users.find(u => {
    const uDigits = normalizePhone(u.phone);
    const uEmail = (u.email || '').toLowerCase().trim();
    return (phoneDigits && uDigits && uDigits === phoneDigits) ||
           (cleanEmail && uEmail && uEmail === cleanEmail);
  });

  if (existing) {
    // Preserve existing customer's original registered name if user input is empty or default
    if (name && name.trim() && name.trim() !== 'Aura Klant') existing.name = name.trim();
    if (cleanEmail) existing.email = cleanEmail;
    if (cleanPhone) existing.phone = cleanPhone;
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(existing));
    broadcastDataChange('USER_LOGGED_IN', existing);
    return { success: true, user: existing };
  }

  // 2. Check past appointments by phone to auto-link history & name
  const appointments = getStoredAppointments();
  const pastAppts = phoneDigits ? appointments.filter(a => {
    const p = normalizePhone(a.phone || a.customerPhone);
    return Boolean(p && p === phoneDigits);
  }) : [];

  const autoName = (name && name.trim() && name.trim() !== 'Aura Klant') 
    ? name.trim() 
    : (pastAppts[0]?.customerName || pastAppts[0]?.name || 'Aura Klant');

  const rawPass = password || '123';
  const newUserId = pastAppts[0]?.customerId || `usr-${Date.now().toString(36)}`;
  const completedCount = pastAppts.length;
  
  const newUser = {
    id: newUserId,
    name: autoName,
    phone: cleanPhone,
    email: cleanEmail || pastAppts[0]?.email || pastAppts[0]?.customerEmail || '',
    passwordHash: "a6c923608adfb9a161b2dd8c8e0066eeebf47d12c39ddf8fcb6b1c744151cdce",
    completedAppointments: completedCount,
    createdAt: new Date().toISOString()
  };

  hashPasswordClient(rawPass).then(computedHash => {
    newUser.passwordHash = computedHash;
    const currentUsers = getStoredUsers();
    const updatedUsers = currentUsers.map(u => u.id === newUserId ? { ...u, passwordHash: computedHash } : u);
    localStorage.setItem(USERS_KEY, JSON.stringify(updatedUsers));
  }).catch(() => {});

  const updated = [newUser, ...users];
  localStorage.setItem(USERS_KEY, JSON.stringify(updated));
  
  const safeSessionUser = {
    id: newUser.id,
    name: newUser.name,
    phone: newUser.phone,
    email: newUser.email,
    completedAppointments: completedCount,
    createdAt: newUser.createdAt
  };
  localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(safeSessionUser));

  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/users/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: newUser.id,
        name: newUser.name,
        phone: newUser.phone,
        email: newUser.email,
        password: rawPass
      })
    }).catch(() => {});
  }

  broadcastDataChange('USER_REGISTERED', safeSessionUser);
  return { success: true, user: safeSessionUser };
}

export function loginUser({ identifier, password, name }) {
  if (!identifier || !identifier.trim()) {
    return { success: false, error: "EMPTY_IDENTIFIER" };
  }

  const users = getStoredUsers();
  const cleanId = identifier.trim().toLowerCase();
  const idDigits = normalizePhone(cleanId);
  const isEmail = cleanId.includes('@');

  // 1. Search in existing registered users
  const user = users.find(u => {
    const uEmail = (u.email || '').toLowerCase().trim();
    const uPhone = (u.phone || '').trim();
    const uDigits = normalizePhone(uPhone);

    if (isEmail) {
      return uEmail === cleanId;
    }
    if (idDigits && idDigits.length >= 6) {
      return uDigits === idDigits;
    }
    return uPhone.toLowerCase().includes(cleanId) || cleanId.includes(uPhone.toLowerCase());
  });

  if (user) {
    const safeUser = {
      id: user.id,
      name: user.name,
      phone: user.phone,
      email: user.email,
      completedAppointments: user.completedAppointments || 0,
      createdAt: user.createdAt
    };
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(safeUser));
    broadcastDataChange('USER_LOGGED_IN', safeUser);

    if (typeof fetch !== 'undefined' && password) {
      fetch(`${getApiBaseUrl()}/api/users/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: user.phone, password })
      }).catch(() => {});
    }
    return { success: true, user: safeUser };
  }

  // 2. Search in previous appointments to auto-restore customer name & profile
  try {
    const appointments = getStoredAppointments();
    const matchingAppt = appointments.find(a => {
      const aEmail = (a.email || a.customerEmail || '').toLowerCase().trim();
      const aPhone = a.phone || a.customerPhone || '';
      const aDigits = normalizePhone(aPhone);
      if (isEmail) return aEmail === cleanId;
      if (idDigits && idDigits.length >= 6) return aDigits === idDigits;
      return false;
    });

    if (matchingAppt) {
      const autoUser = {
        id: `usr-${Date.now().toString(36)}`,
        name: matchingAppt.customerName || matchingAppt.name || name || 'Aura Klant',
        phone: matchingAppt.customerPhone || matchingAppt.phone || identifier.trim(),
        email: matchingAppt.customerEmail || matchingAppt.email || (isEmail ? cleanId : ''),
        completedAppointments: appointments.filter(a => {
          const p = a.phone || a.customerPhone || '';
          return normalizePhone(p) === idDigits;
        }).length || 1,
        createdAt: new Date().toISOString()
      };
      const updated = [autoUser, ...users];
      localStorage.setItem(USERS_KEY, JSON.stringify(updated));
      localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(autoUser));
      broadcastDataChange('USER_LOGGED_IN', autoUser);
      return { success: true, user: autoUser };
    }
  } catch (e) {}

  // 3. If identifier looks like a phone (>=7 digits) or email, auto-create seamless account so customer is NEVER blocked!
  if ((idDigits && idDigits.length >= 7) || isEmail) {
    const autoUser = {
      id: `usr-${Date.now().toString(36)}`,
      name: (name && name.trim()) || (isEmail ? cleanId.split('@')[0] : `Klant 06-${idDigits.slice(-4)}`),
      phone: identifier.trim(),
      email: isEmail ? cleanId : '',
      completedAppointments: 0,
      createdAt: new Date().toISOString()
    };
    const updated = [autoUser, ...users];
    localStorage.setItem(USERS_KEY, JSON.stringify(updated));
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(autoUser));
    broadcastDataChange('USER_REGISTERED', autoUser);
    return { success: true, user: autoUser, isAutoCreated: true };
  }

  return { success: false, error: "USER_NOT_FOUND" };
}

export function loginWithSocial({ provider, email, name, avatar, socialId }) {
  if (!provider) return { success: false, error: "PROVIDER_MISSING" };
  const users = getStoredUsers();
  const cleanEmail = (email || '').toLowerCase().trim();
  const validProvider = provider === 'facebook' ? 'facebook' : 'google';

  // 1. Check existing user by email or socialId
  let user = users.find(u => {
    if (socialId && u.socialId === socialId && u.socialProvider === validProvider) return true;
    if (cleanEmail && (u.email || '').toLowerCase().trim() === cleanEmail) return true;
    return false;
  });

  if (!user) {
    // Also check past appointments to link past haircut history
    const appointments = getStoredAppointments();
    const pastAppts = cleanEmail ? appointments.filter(a => (a.email || a.customerEmail || '').toLowerCase().trim() === cleanEmail) : [];

    const defaultName = name && name.trim() ? name.trim() : (cleanEmail ? cleanEmail.split('@')[0] : (validProvider === 'google' ? 'Google Müşteri' : 'Facebook Müşteri'));
    
    user = {
      id: `usr-soc-${Date.now().toString(36)}`,
      name: defaultName,
      email: cleanEmail || `${validProvider}_user_${Date.now()}@aurahair.nl`,
      phone: pastAppts[0]?.phone || pastAppts[0]?.customerPhone || '',
      avatar: avatar || '',
      socialProvider: validProvider,
      socialId: socialId || `soc_${Date.now()}`,
      completedAppointments: pastAppts.length,
      createdAt: new Date().toISOString()
    };
    users.unshift(user);
    localStorage.setItem(USERS_KEY, JSON.stringify(users));

    if (typeof fetch !== 'undefined') {
      fetch(`${getApiBaseUrl()}/api/users/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: user.id,
          name: user.name,
          phone: user.phone,
          email: user.email,
          socialProvider: user.socialProvider,
          socialId: user.socialId
        })
      }).catch(() => {});
    }
  } else {
    // Update existing user with social info
    user.socialProvider = validProvider;
    if (socialId && !user.socialId) user.socialId = socialId;
    if (avatar && !user.avatar) user.avatar = avatar;
    if (name && (!user.name || user.name === 'Aura Klant')) user.name = name;
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
  }

  const safeUser = {
    id: user.id,
    name: user.name,
    phone: user.phone || '',
    email: user.email || '',
    avatar: user.avatar || '',
    socialProvider: user.socialProvider || validProvider,
    completedAppointments: user.completedAppointments || 0,
    createdAt: user.createdAt
  };
  localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(safeUser));
  broadcastDataChange('USER_LOGGED_IN', safeUser);
  return { success: true, user: safeUser };
}

export function getCurrentUser() {
  try {
    const data = localStorage.getItem(CURRENT_USER_KEY);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    return null;
  }
}

export function logoutUser() {
  localStorage.removeItem(CURRENT_USER_KEY);
}

// ==================== LOYALTY CARD CALCULATION ====================

export function getUserLoyaltyInfo(userId, phone) {
  const appointments = getStoredAppointments();
  const users = getStoredUsers();
  const phoneDigits = normalizePhone(phone);

  const user = users.find(u => {
    if (userId && u.id === userId) return true;
    const uDigits = normalizePhone(u.phone);
    return Boolean(phoneDigits && uDigits && uDigits === phoneDigits);
  });
  
  if (!user) {
    return {
      totalBookings: 0,
      stamps: 0,
      currentStamps: 0,
      remainingForReward: 10,
      isRewardEligible: false,
      rewardType: 'free_haircut',
      rewardsEarned: 0,
      isRegistered: false
    };
  }

  const userAppointments = appointments.filter(a => {
    if (a.customerId === user.id) return true;
    const aPhone = a.phone || a.customerPhone || '';
    const aDigits = normalizePhone(aPhone);
    const uDigits = normalizePhone(user.phone);
    return Boolean(aDigits && uDigits && aDigits === uDigits);
  });

  const completedApps = userAppointments.filter(a => a.status === 'completed' || a.isPaid);
  const count = Math.max(user.completedAppointments || 0, completedApps.length, userAppointments.length);
  const currentStamps = count % 10;
  const is10thRewardEligible = currentStamps === 9 || (count > 0 && count % 10 === 0);
  const rewardsEarned = Math.floor(count / 10);

  return {
    totalBookings: count,
    stamps: currentStamps,
    currentStamps: currentStamps,
    remainingForReward: 10 - currentStamps,
    isRewardEligible: is10thRewardEligible,
    rewardType: 'free_haircut',
    rewardsEarned,
    isRegistered: true
  };
}

export function getAllCustomersWithLoyalty() {
  const users = getStoredUsers();
  const appointments = getStoredAppointments();

  const seenPhones = new Set();
  const result = [];

  for (const user of users) {
    const uDigits = normalizePhone(user.phone);
    if (uDigits) seenPhones.add(uDigits);

    const userAppointments = appointments.filter(a => {
      if (a.customerId === user.id) return true;
      const aPhone = a.phone || a.customerPhone || '';
      const aDigits = normalizePhone(aPhone);
      return Boolean(uDigits && aDigits && uDigits === aDigits);
    });

    const totalSpend = userAppointments
      .filter(a => a.status === 'completed' || a.status === 'confirmed' || a.isPaid)
      .reduce((sum, a) => sum + (Number(a.paidAmount) || Number(a.totalPrice) || 0), 0);

    const completedApps = userAppointments.filter(a => a.status === 'completed' || a.isPaid);
    const count = Math.max(user.completedAppointments || 0, completedApps.length, userAppointments.length);
    const currentStamps = count % 10;
    const is10thRewardEligible = currentStamps === 9 || (count > 0 && count % 10 === 0);
    const remaining = 10 - currentStamps;
    const lastAppointment = userAppointments.sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null;

    result.push({
      ...user,
      totalBookings: count,
      totalSpend,
      stamps: currentStamps,
      currentStamps,
      remainingForReward: remaining,
      isRewardEligible: is10thRewardEligible,
      lastAppointmentDate: lastAppointment ? lastAppointment.date : null,
      appointments: userAppointments
    });
  }

  // Also include customers who booked appointments but haven't registered an account yet
  for (const app of appointments) {
    const aPhone = app.phone || app.customerPhone || '';
    const aDigits = normalizePhone(aPhone);
    if (!aDigits || seenPhones.has(aDigits)) continue;
    seenPhones.add(aDigits);

    const relatedApps = appointments.filter(a => {
      const p = normalizePhone(a.phone || a.customerPhone);
      return Boolean(p && p === aDigits);
    });

    const totalSpend = relatedApps
      .filter(a => a.status === 'completed' || a.status === 'confirmed' || a.isPaid)
      .reduce((sum, a) => sum + (Number(a.paidAmount) || Number(a.totalPrice) || 0), 0);

    const count = relatedApps.length;
    const currentStamps = count % 10;
    const lastApp = relatedApps.sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null;

    result.push({
      id: app.customerId || `cust-${aDigits}`,
      name: app.customerName || app.name || `Klant 06-${aDigits.slice(-4)}`,
      phone: aPhone,
      email: app.email || app.customerEmail || '',
      completedAppointments: count,
      createdAt: app.createdAt || new Date().toISOString(),
      isRegistered: Boolean(app.customerId),
      totalBookings: count,
      totalSpend,
      stamps: currentStamps,
      currentStamps,
      remainingForReward: 10 - currentStamps,
      isRewardEligible: currentStamps === 9 || (count > 0 && count % 10 === 0),
      lastAppointmentDate: lastApp ? lastApp.date : null,
      appointments: relatedApps
    });
  }

  return result;
}

export function updateCustomerStamps(userId, newCount) {
  const users = getStoredUsers();
  const updatedUsers = users.map(u => {
    if (u.id === userId) {
      return { ...u, completedAppointments: Math.max(0, newCount) };
    }
    return u;
  });
  localStorage.setItem(USERS_KEY, JSON.stringify(updatedUsers));
  
  const currentUser = getCurrentUser();
  if (currentUser && currentUser.id === userId) {
    currentUser.completedAppointments = Math.max(0, newCount);
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(currentUser));
  }

  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/users/stamps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, stamps: newCount })
    }).catch(() => {});
  }

  broadcastDataChange('USER_STAMPS_UPDATED', { userId, stamps: newCount });
  return getAllCustomersWithLoyalty();
}

export function deleteCustomer(userId) {
  try {
    const users = getStoredUsers();
    const updatedUsers = users.filter(u => u.id !== userId);
    localStorage.setItem(USERS_KEY, JSON.stringify(updatedUsers));

    const currentUser = getCurrentUser();
    if (currentUser && currentUser.id === userId) {
      localStorage.removeItem(CURRENT_USER_KEY);
    }

    if (typeof fetch !== 'undefined') {
      fetch(`${getApiBaseUrl()}/api/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      }).then(r => r.json()).then(data => {
        if (data?.users) {
          localStorage.setItem(USERS_KEY, JSON.stringify(data.users));
          broadcastDataChange('USERS_SYNCED', data.users);
        }
      }).catch(() => {
        fetch(`${getApiBaseUrl()}/api/users/delete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId })
        }).catch(() => {});
      });
    }

    broadcastDataChange('CUSTOMER_DELETED', userId);
    return getAllCustomersWithLoyalty();
  } catch (e) {
    console.error("Failed to delete customer:", e);
    return getAllCustomersWithLoyalty();
  }
}

export function clearCustomerData() {
  try {
    localStorage.setItem(USERS_KEY, JSON.stringify([]));
    broadcastDataChange('USERS_CLEARED', []);
    if (typeof fetch !== 'undefined') {
      fetch(`${getApiBaseUrl()}/api/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'clear_customers' })
      }).catch(() => {});
    }
    return [];
  } catch (e) {
    console.error('Failed to clear customers:', e);
    return [];
  }
}

// ==================== APPOINTMENTS ====================

export function getStoredAppointments() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
      return [];
    }
    const parsed = JSON.parse(data);
    // Filter out any legacy dummy sample appointments and normalize all fields
    const cleanList = Array.isArray(parsed) ? parsed.filter(a => !a.id?.startsWith('APT-849')).map(a => ({
      ...a,
      phone: a.phone || a.customerPhone || '',
      customerPhone: a.customerPhone || a.phone || '',
      email: a.email || a.customerEmail || '',
      customerEmail: a.customerEmail || a.email || '',
      barber: a.barber || a.barberName || 'Aura Barber',
      barberName: a.barberName || a.barber || 'Aura Barber',
      customerName: a.customerName || a.name || 'Misafir'
    })) : [];
    if (Array.isArray(parsed) && cleanList.length !== parsed.length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanList));
    }
    return cleanList;
  } catch (e) {
    return [];
  }
}

export function saveAppointment(appointmentData) {
  try {
    const current = getStoredAppointments();
    const newId = appointmentData.id || `APT-${Math.floor(10000 + Math.random() * 90000)}`;
    
    const totalPrice = Number(appointmentData.totalPrice || 0);
    const isDepositRequired = totalPrice > 100;
    const depositAmount = isDepositRequired ? 50 : 0;
    const payAtSalonAmount = isDepositRequired ? Math.max(0, totalPrice - 50) : totalPrice;
    const hasStartingPrice = Boolean(appointmentData.hasStartingPrice || (appointmentData.services || []).some(s => s.isStartingPrice));

    const customerName = appointmentData.customerName || appointmentData.name || "Misafir";
    const phone = appointmentData.phone || appointmentData.customerPhone || "";
    const email = appointmentData.email || appointmentData.customerEmail || "";
    const barberName = appointmentData.barberName || appointmentData.barber || "Aura Barber";
    const barberId = appointmentData.barberId || "barber-aura";

    // Auto-link customerId by phone if missing
    let resolvedCustomerId = appointmentData.customerId || null;
    if (!resolvedCustomerId && phone) {
      const existingCust = lookupCustomerByPhone(phone);
      if (existingCust) resolvedCustomerId = existingCust.id;
    }

    const newAppointment = {
      id: newId,
      customerId: resolvedCustomerId,
      status: appointmentData.status || "confirmed",
      createdAt: new Date().toISOString(),
      isDepositRequired,
      depositAmount,
      payAtSalonAmount,
      depositStatus: isDepositRequired ? (appointmentData.depositStatus || "paid") : "not_required",
      hasStartingPrice,
      ...appointmentData,
      customerId: resolvedCustomerId,
      customerName,
      phone,
      customerPhone: phone,
      email,
      customerEmail: email,
      barber: barberName,
      barberName,
      barberId,
      totalPrice,
      depositAmount,
      payAtSalonAmount
    };

    const updated = [newAppointment, ...current.filter(a => a.id !== newAppointment.id)];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

    if (resolvedCustomerId) {
      const users = getStoredUsers();
      const updatedUsers = users.map(u => {
        if (u.id === resolvedCustomerId) {
          return { ...u, completedAppointments: (u.completedAppointments || 0) + 1 };
        }
        return u;
      });
      localStorage.setItem(USERS_KEY, JSON.stringify(updatedUsers));
      
      const currentUser = getCurrentUser();
      if (currentUser && currentUser.id === resolvedCustomerId) {
        currentUser.completedAppointments = (currentUser.completedAppointments || 0) + 1;
        localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(currentUser));
      }
    }

    // 1. Instant local cross-tab event
    broadcastDataChange('APPOINTMENT_SAVED', newAppointment);

    // 2. Real-time server-side API synchronization for ALL devices & tablets on Wi-Fi
    if (typeof fetch !== 'undefined') {
      const apiUrl = `${getApiBaseUrl()}/api/appointments`;
      fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAppointment)
      }).then(r => r.json()).then(data => {
        if (data && data.appointments) {
          const normalized = data.appointments.map(a => ({
            ...a,
            phone: a.phone || a.customerPhone || '',
            customerPhone: a.customerPhone || a.phone || '',
            email: a.email || a.customerEmail || '',
            customerEmail: a.customerEmail || a.email || '',
            barber: a.barber || a.barberName || 'Aura Barber',
            barberName: a.barberName || a.barber || 'Aura Barber',
            customerName: a.customerName || a.name || 'Misafir'
          }));
          localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
          broadcastDataChange('APPOINTMENTS_SYNCED', normalized);
        }
      }).catch(err => {
        console.warn('API sync warning:', err);
      });
    }

    return newAppointment;
  } catch (e) {
    console.error("Failed to save appointment:", e);
    return null;
  }
}

export function updateAppointmentStatus(id, newStatus) {
  try {
    const current = getStoredAppointments();
    let targetApp = null;
    const updated = current.map(item => {
      if (item.id === id) {
        const isNowCompleted = newStatus === 'completed';
        targetApp = { 
          ...item, 
          status: newStatus,
          isPaid: isNowCompleted ? true : item.isPaid,
          paidAmount: (isNowCompleted && !item.paidAmount) ? Number(item.totalPrice || 0) : item.paidAmount,
          paidAt: (isNowCompleted && !item.paidAt) ? new Date().toISOString() : item.paidAt
        };
        return targetApp;
      }
      return item;
    });

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    broadcastDataChange('APPOINTMENT_STATUS_UPDATED', { id, status: newStatus });

    // If marked completed, update customer stats and loyalty stamps (by customerId or phone)
    if (newStatus === 'completed' && targetApp) {
      const targetPhone = targetApp.phone || targetApp.customerPhone || '';
      const phoneDigits = normalizePhone(targetPhone);
      const users = getStoredUsers();
      let matchedUserId = targetApp.customerId;

      const updatedUsers = users.map(u => {
        const uDigits = normalizePhone(u.phone);
        const isMatch = (matchedUserId && u.id === matchedUserId) || (phoneDigits && uDigits && uDigits === phoneDigits);
        if (isMatch) {
          if (!matchedUserId) matchedUserId = u.id;
          return { ...u, completedAppointments: (u.completedAppointments || 0) + 1 };
        }
        return u;
      });

      localStorage.setItem(USERS_KEY, JSON.stringify(updatedUsers));
      broadcastDataChange('USERS_UPDATED', updatedUsers);

      const currentUser = getCurrentUser();
      if (currentUser && ((matchedUserId && currentUser.id === matchedUserId) || (phoneDigits && normalizePhone(currentUser.phone) === phoneDigits))) {
        currentUser.completedAppointments = (currentUser.completedAppointments || 0) + 1;
        localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(currentUser));
      }

      if (matchedUserId) {
        const matched = updatedUsers.find(u => u.id === matchedUserId);
        if (matched && typeof fetch !== 'undefined') {
          fetch(`${getApiBaseUrl()}/api/users/stamps`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: matchedUserId, stamps: matched.completedAppointments })
          }).catch(() => {});
        }
      }
    }

    if (typeof fetch !== 'undefined') {
      const apiUrl = `${getApiBaseUrl()}/api/appointments/${encodeURIComponent(id)}`;
      fetch(apiUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, ...(targetApp || {}) })
      }).then(r => r.json()).then(data => {
        if (data?.appointments) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data.appointments));
          broadcastDataChange('APPOINTMENTS_SYNCED', data.appointments);
        }
      }).catch(() => {});
    }

    return updated;
  } catch (e) {
    console.error("Failed to update status:", e);
    return getStoredAppointments();
  }
}

export function deleteAppointment(id) {
  try {
    const current = getStoredAppointments();
    const updated = current.filter(item => item.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    broadcastDataChange('APPOINTMENT_DELETED', id);

    if (typeof fetch !== 'undefined') {
      const apiUrl = `${getApiBaseUrl()}/api/appointments/${encodeURIComponent(id)}`;
      fetch(apiUrl, {
        method: 'DELETE'
      }).then(r => r.json()).then(data => {
        if (data?.appointments) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data.appointments));
          broadcastDataChange('APPOINTMENTS_SYNCED', data.appointments);
        }
      }).catch(() => {});
    }

    return updated;
  } catch (e) {
    console.error("Failed to delete appointment:", e);
    return getStoredAppointments();
  }
}

export function updateAppointmentPrice(id, { newTotalPrice, originalPrice, priceAdjustment, adjustmentReason, notes }) {
  try {
    const current = getStoredAppointments();
    let targetApp = null;
    const updated = current.map(item => {
      if (item.id === id) {
        targetApp = { 
          ...item, 
          originalTotalPrice: item.originalTotalPrice !== undefined ? item.originalTotalPrice : (originalPrice || item.totalPrice),
          totalPrice: Number(newTotalPrice),
          priceAdjustment: Number(priceAdjustment || 0),
          adjustmentReason: adjustmentReason || '',
          priceAdjustedAt: new Date().toISOString(),
          notes: notes !== undefined ? notes : item.notes
        };
        return targetApp;
      }
      return item;
    });

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    broadcastDataChange('APPOINTMENT_PRICE_UPDATED', targetApp);

    if (typeof fetch !== 'undefined') {
      const apiUrl = `${getApiBaseUrl()}/api/appointments/${encodeURIComponent(id)}`;
      fetch(apiUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(targetApp)
      }).then(r => r.json()).then(data => {
        if (data?.appointments) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data.appointments));
          broadcastDataChange('APPOINTMENTS_SYNCED', data.appointments);
        }
      }).catch(() => {});
    }

    return updated;
  } catch (e) {
    console.error("Failed to update appointment price:", e);
    return getStoredAppointments();
  }
}

export function recordAppointmentPayment(id, { paidAmount, paymentMethod, isCompleted = true, notes }) {
  try {
    const current = getStoredAppointments();
    let targetApp = null;
    const updated = current.map(item => {
      if (item.id === id) {
        targetApp = { 
          ...item, 
          isPaid: true,
          paymentStatus: 'paid',
          depositStatus: 'paid',
          isDepositPaid: true,
          payAtSalonAmount: 0,
          paidAmount: Number(paidAmount !== undefined ? paidAmount : item.totalPrice),
          paymentMethod: paymentMethod || 'pin',
          paidAt: new Date().toISOString(),
          status: isCompleted ? 'completed' : item.status,
          notes: notes !== undefined ? notes : item.notes
        };
        return targetApp;
      }
      return item;
    });

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    broadcastDataChange('APPOINTMENT_PAYMENT_RECORDED', targetApp);

    if (isCompleted && targetApp) {
      const targetPhone = targetApp.phone || targetApp.customerPhone || '';
      const phoneDigits = normalizePhone(targetPhone);
      const users = getStoredUsers();
      let matchedUserId = targetApp.customerId;

      const updatedUsers = users.map(u => {
        const uDigits = normalizePhone(u.phone);
        const isMatch = (matchedUserId && u.id === matchedUserId) || (phoneDigits && uDigits && uDigits === phoneDigits);
        if (isMatch) {
          if (!matchedUserId) matchedUserId = u.id;
          return { ...u, completedAppointments: (u.completedAppointments || 0) + 1 };
        }
        return u;
      });

      localStorage.setItem(USERS_KEY, JSON.stringify(updatedUsers));
      broadcastDataChange('USERS_UPDATED', updatedUsers);

      const currentUser = getCurrentUser();
      if (currentUser && ((matchedUserId && currentUser.id === matchedUserId) || (phoneDigits && normalizePhone(currentUser.phone) === phoneDigits))) {
        currentUser.completedAppointments = (currentUser.completedAppointments || 0) + 1;
        localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(currentUser));
      }

      if (matchedUserId) {
        const matched = updatedUsers.find(u => u.id === matchedUserId);
        if (matched && typeof fetch !== 'undefined') {
          fetch(`${getApiBaseUrl()}/api/users/stamps`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: matchedUserId, stamps: matched.completedAppointments })
          }).catch(() => {});
        }
      }
    }

    if (typeof fetch !== 'undefined') {
      const apiUrl = `${getApiBaseUrl()}/api/appointments/${encodeURIComponent(id)}`;
      fetch(apiUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(targetApp)
      }).then(r => r.json()).then(data => {
        if (data?.appointments) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data.appointments));
          broadcastDataChange('APPOINTMENTS_SYNCED', data.appointments);
        }
      }).catch(() => {});
    }

    return updated;
  } catch (e) {
    console.error("Failed to record appointment payment:", e);
    return getStoredAppointments();
  }
}

// ==================== SYSTEM RESET & DANGER ZONE ====================

export function clearAllAppointments() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    broadcastDataChange('APPOINTMENTS_CLEARED', []);
    if (typeof fetch !== 'undefined') {
      fetch(`${getApiBaseUrl()}/api/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'appointments' })
      }).catch(() => {});
    }
    return [];
  } catch (e) {
    console.error('Failed to clear appointments:', e);
    return [];
  }
}

export function resetCustomerData() {
  try {
    localStorage.setItem(USERS_KEY, JSON.stringify(INITIAL_USERS));
    broadcastDataChange('USERS_RESET', INITIAL_USERS);
    if (typeof fetch !== 'undefined') {
      fetch(`${getApiBaseUrl()}/api/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'customers' })
      }).catch(() => {});
    }
    return getAllCustomersWithLoyalty();
  } catch (e) {
    console.error('Failed to reset customers:', e);
    return [];
  }
}

export function resetBlockedSlots() {
  try {
    localStorage.setItem(BLOCKED_SLOTS_KEY, JSON.stringify([]));
    broadcastDataChange('BLOCKED_SLOTS_RESET', []);
    if (typeof fetch !== 'undefined') {
      fetch(`${getApiBaseUrl()}/api/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'slots' })
      }).catch(() => {});
    }
    return [];
  } catch (e) {
    console.error('Failed to reset blocked slots:', e);
    return [];
  }
}

export function resetPromoCodesAndGifts() {
  try {
    localStorage.setItem(PROMO_CODES_KEY, JSON.stringify(INITIAL_PROMO_CODES));
    localStorage.setItem(GIFT_CARDS_KEY, JSON.stringify(INITIAL_GIFT_CARDS));
    broadcastDataChange('PROMOS_RESET', INITIAL_PROMO_CODES);
    if (typeof fetch !== 'undefined') {
      fetch(`${getApiBaseUrl()}/api/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'promos' })
      }).catch(() => {});
      fetch(`${getApiBaseUrl()}/api/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'giftcards' })
      }).catch(() => {});
    }
  } catch (e) {
    console.error('Failed to reset promos:', e);
  }
}

export function resetEntireSystem() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    localStorage.setItem(USERS_KEY, JSON.stringify(INITIAL_USERS));
    localStorage.setItem(BLOCKED_SLOTS_KEY, JSON.stringify([]));
    localStorage.setItem(PROMO_CODES_KEY, JSON.stringify(INITIAL_PROMO_CODES));
    localStorage.setItem(GIFT_CARDS_KEY, JSON.stringify(INITIAL_GIFT_CARDS));
    broadcastDataChange('SYSTEM_FACTORY_RESET', {});
    if (typeof fetch !== 'undefined') {
      fetch(`${getApiBaseUrl()}/api/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'all' })
      }).catch(() => {});
    }
    return true;
  } catch (e) {
    console.error('Failed to perform system factory reset:', e);
    return false;
  }
}

/**
 * Generate WhatsApp confirmation text in current language
 */
export function createWhatsAppBookingUrl(appointment, lang = 'nl') {
  const currentLang = lang || 'nl';
  const servicesList = (appointment.services || []).map(s => {
    const sName = typeof s.name === 'object' ? (s.name[currentLang] || s.name.nl || s.name.tr) : s.name;
    const startNote = s.isStartingPrice ? (currentLang === 'tr' ? ' (Başlangıç)' : currentLang === 'nl' ? ' (Vanaf)' : ' (Starting)') : '';
    return `• ${sName}${startNote} (€${s.price})`;
  }).join('\n');
  
  const dateFormatted = formatLocalizedDate(appointment.date, currentLang);
  const mapsUrl = "https://maps.google.com/?q=Samuel+Esmeijerplein+3,+3067+HP+Rotterdam";

  let discountNote = "";
  if (appointment.isLoyaltyDiscountApplied) {
    const freeCutAmount = appointment.discountAmount || appointment.loyaltyDiscountAmount || 0;
    const amountStr = freeCutAmount > 0 ? ` (-€${freeCutAmount})` : '';
    discountNote = currentLang === 'tr' 
      ? `🎁 *10. Randevu Sadakat Hediyesi:* 1 Adet Bedava Saç Kesimi Uygulandı!${amountStr}\n`
      : currentLang === 'en'
      ? `🎁 *10th Booking Loyalty Reward:* 1 Free Haircut Applied!${amountStr}\n`
      : `🎁 *10e Afspraak Beloning:* 1 Gratis Knipbeurt Toegepast!${amountStr}\n`;
  } else if (appointment.promoCode) {
    discountNote = currentLang === 'tr'
      ? `🎟️ *İndirim Kuponu (${appointment.promoCode}):* -€${appointment.discountAmount}\n`
      : `🎟️ *Kortingscode (${appointment.promoCode}):* -€${appointment.discountAmount}\n`;
  }

  const totalPrice = Number(appointment.totalPrice || 0);
  const isDepositRequired = Boolean(appointment.isDepositRequired || totalPrice > 100);
  const depositAmount = isDepositRequired ? 50 : 0;
  const payAtSalonAmount = isDepositRequired ? Math.max(0, totalPrice - 50) : totalPrice;
  const hasStarting = Boolean(appointment.hasStartingPrice || (appointment.services || []).some(s => s.isStartingPrice));

  let message = "";
  if (currentLang === 'tr') {
    let paymentInfo = "";
    if (isDepositRequired) {
      paymentInfo = 
        `💰 *Toplam Tutar:* €${totalPrice}${hasStarting ? ' (Başlangıç Tahmini)' : ''}\n` +
        `💳 *Ön Ödeme (Kapora):* €50,00 (100 € üzeri randevu güvencesi)\n` +
        `🏢 *Salonda Ödenecek Kalan:* €${payAtSalonAmount}${hasStarting ? ' (Saç analizine göre salonda netleşir)' : ''}\n`;
    } else {
      paymentInfo = 
        `💰 *Toplam Tutar:* €${totalPrice}${hasStarting ? ' (Başlangıç)' : ''}\n` +
        `🏢 *Ödeme:* Randevu esnasında salonda tahsil edilecektir (Nakit / Pin / iDEAL)\n`;
    }

    message = 
      `👋 *Merhaba Aura Hair Rotterdam,*\n\n` +
      `Web siteniz üzerinden yeni bir randevu aldım:\n\n` +
      `📌 *Randevu Kodu:* ${appointment.id}\n` +
      `👤 *Müşteri:* ${appointment.customerName}\n` +
      `📱 *Telefon:* ${appointment.phone}\n` +
      `📅 *Tarih & Saat:* ${dateFormatted} - ${appointment.time}\n` +
      `⏳ *Tahmini Süre:* ${appointment.totalDuration || appointment.duration || 45} dakika\n` +
      discountNote +
      paymentInfo +
      `\n✂️ *Hizmet(ler):*\n${servicesList}` +
      (appointment.notes ? `\n💬 *Not:* ${appointment.notes}\n` : '\n') +
      `\n📍 *Adres & Yol Tarifi:*\nSamuel Esmeijerplein 3, 3067 HP Rotterdam\n🗺️ *Haritada Aç / Doğrudan Navigasyon:* ${mapsUrl}\n\n` +
      `Randevumu teyit eder misiniz? Teşekkürler!`;
  } else if (currentLang === 'en') {
    let paymentInfo = "";
    if (isDepositRequired) {
      paymentInfo = 
        `💰 *Total Amount:* €${totalPrice}${hasStarting ? ' (Estimated Starting)' : ''}\n` +
        `💳 *Deposit Required (Over €100):* €50.00\n` +
        `🏢 *Remaining at Salon:* €${payAtSalonAmount}${hasStarting ? ' (Finalized after hair consultation)' : ''}\n`;
    } else {
      paymentInfo = 
        `💰 *Total Amount:* €${totalPrice}${hasStarting ? ' (Starting)' : ''}\n` +
        `🏢 *Payment:* Payable at the salon during appointment (Card / Cash / iDEAL)\n`;
    }

    message = 
      `👋 *Hello Aura Hair Rotterdam,*\n\n` +
      `I have just booked an appointment via your website:\n\n` +
      `📌 *Booking Code:* ${appointment.id}\n` +
      `👤 *Client:* ${appointment.customerName}\n` +
      `📱 *Phone:* ${appointment.phone}\n` +
      `📅 *Date & Time:* ${dateFormatted} at ${appointment.time}\n` +
      `⏳ *Duration:* ${appointment.totalDuration || appointment.duration || 45} minutes\n` +
      discountNote +
      paymentInfo +
      `\n✂️ *Selected Services:*\n${servicesList}` +
      (appointment.notes ? `\n💬 *Notes:* ${appointment.notes}\n` : '\n') +
      `\n📍 *Location & Directions:*\nSamuel Esmeijerplein 3, 3067 HP Rotterdam\n🗺️ *Open in Maps / GPS Navigation:* ${mapsUrl}\n\n` +
      `Looking forward to my appointment!`;
  } else {
    // Standard Dutch (Default for Rotterdam salon)
    let paymentInfo = "";
    if (isDepositRequired) {
      paymentInfo = 
        `💰 *Totaalbedrag:* €${totalPrice}${hasStarting ? ' (Vanaf schatting)' : ''}\n` +
        `💳 *Aanbetaling / Borg (> €100):* €50,00\n` +
        `🏢 *Resterend in salon:* €${payAtSalonAmount}${hasStarting ? ' (Definitieve prijs na haaranalyse)' : ''}\n`;
    } else {
      paymentInfo = 
        `💰 *Totaalbedrag:* €${totalPrice}${hasStarting ? ' (Vanaf prijs)' : ''}\n` +
        `🏢 *Betaling:* Te voldoen in de salon tijdens uw afspraak (Pin / Contant / iDEAL)\n`;
    }

    message = 
      `👋 *Beste Aura Hair Rotterdam,*\n\n` +
      `Ik heb zojuist een afspraak gemaakt via de website:\n\n` +
      `📌 *Afspraakcode:* ${appointment.id}\n` +
      `👤 *Klant:* ${appointment.customerName}\n` +
      `📱 *Telefoon:* ${appointment.phone}\n` +
      `📅 *Datum & Tijd:* ${dateFormatted} om ${appointment.time} uur\n` +
      `⏳ *Geschatte Duur:* ${appointment.totalDuration || appointment.duration || 45} minuten\n` +
      discountNote +
      paymentInfo +
      `\n✂️ *Gekozen Behandeling(en):*\n${servicesList}` +
      (appointment.notes ? `\n💬 *Opmerkingen:* ${appointment.notes}\n` : '\n') +
      `\n📍 *Adres & Routebeschrijving:*\nSamuel Esmeijerplein 3, 3067 HP Rotterdam\n🗺️ *Direct Navigeren via Google Maps:* ${mapsUrl}\n\n` +
      `Graag tot ziens aan het Samuel Esmeijerplein 3, Rotterdam!`;
  }

  return `https://wa.me/${salonInfo.whatsappRaw}?text=${encodeURIComponent(message)}`;
}

// ==================== DYNAMIC SERVICES & PRICES ENGINE ====================

const SERVICES_KEY = "aurahair_services_v1";

export function getStoredServices() {
  try {
    const data = localStorage.getItem(SERVICES_KEY);
    if (!data) {
      localStorage.setItem(SERVICES_KEY, JSON.stringify(defaultServices));
      return defaultServices;
    }
    const parsed = JSON.parse(data);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : defaultServices;
  } catch (e) {
    return defaultServices;
  }
}

export function saveStoredServices(servicesList) {
  if (!Array.isArray(servicesList)) return getStoredServices();
  localStorage.setItem(SERVICES_KEY, JSON.stringify(servicesList));

  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/services`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(servicesList)
    }).catch(() => {});
  }

  broadcastDataChange('SERVICES_UPDATED', servicesList);
  return servicesList;
}

export function saveStoredService(service) {
  const all = getStoredServices();
  const id = service.id || `srv-${Date.now().toString(36)}`;
  const updatedItem = {
    ...service,
    id,
    price: Number(service.price) || 0,
    duration: Number(service.duration) || 30
  };

  const existingIdx = all.findIndex(s => s.id === id);
  let updated;
  if (existingIdx >= 0) {
    updated = [...all];
    updated[existingIdx] = updatedItem;
  } else {
    updated = [updatedItem, ...all];
  }

  return saveStoredServices(updated);
}

export function deleteStoredService(serviceId) {
  const all = getStoredServices();
  const updated = all.filter(s => s.id !== serviceId);
  return saveStoredServices(updated);
}

export function resetStoredServices() {
  localStorage.setItem(SERVICES_KEY, JSON.stringify(defaultServices));
  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/services/reset`, { method: 'POST' }).catch(() => {});
  }
  broadcastDataChange('SERVICES_UPDATED', defaultServices);
  return defaultServices;
}

export async function fetchServicesFromServer() {
  if (typeof fetch === 'undefined') return getStoredServices();
  try {
    const res = await fetch(`${getApiBaseUrl()}/api/services`);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        localStorage.setItem(SERVICES_KEY, JSON.stringify(data));
        broadcastDataChange('SERVICES_SYNCED', data);
        return data;
      }
    }
  } catch (err) {
    console.warn('Services sync warning:', err);
  }
  return getStoredServices();
}

// ==================== DYNAMIC GALLERY (PHOTOS & VIDEOS) ENGINE ====================

const GALLERY_KEY = "aurahair_gallery_v1";

export function getStoredGallery() {
  try {
    const data = localStorage.getItem(GALLERY_KEY);
    if (!data) {
      localStorage.setItem(GALLERY_KEY, JSON.stringify(defaultGalleryItems));
      return defaultGalleryItems;
    }
    const parsed = JSON.parse(data);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : defaultGalleryItems;
  } catch (e) {
    return defaultGalleryItems;
  }
}

export function saveStoredGallery(galleryList) {
  if (!Array.isArray(galleryList)) return getStoredGallery();
  localStorage.setItem(GALLERY_KEY, JSON.stringify(galleryList));

  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/gallery`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(galleryList)
    }).catch(() => {});
  }

  broadcastDataChange('GALLERY_UPDATED', galleryList);
  return galleryList;
}

export function saveStoredGalleryItem(item) {
  const all = getStoredGallery();
  const id = item.id || `g-${Date.now().toString(36)}`;
  const updatedItem = {
    ...item,
    id,
    type: item.type || (item.videoUrl ? 'video' : 'image')
  };

  const existingIdx = all.findIndex(g => g.id === id);
  let updated;
  if (existingIdx >= 0) {
    updated = [...all];
    updated[existingIdx] = updatedItem;
  } else {
    updated = [updatedItem, ...all];
  }

  return saveStoredGallery(updated);
}

export function deleteStoredGalleryItem(itemId) {
  const all = getStoredGallery();
  const updated = all.filter(g => g.id !== itemId);
  return saveStoredGallery(updated);
}

export function resetStoredGallery() {
  localStorage.setItem(GALLERY_KEY, JSON.stringify(defaultGalleryItems));
  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/gallery/reset`, { method: 'POST' }).catch(() => {});
  }
  broadcastDataChange('GALLERY_UPDATED', defaultGalleryItems);
  return defaultGalleryItems;
}

export async function fetchGalleryFromServer() {
  if (typeof fetch === 'undefined') return getStoredGallery();
  try {
    const res = await fetch(`${getApiBaseUrl()}/api/gallery`);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        localStorage.setItem(GALLERY_KEY, JSON.stringify(data));
        broadcastDataChange('GALLERY_SYNCED', data);
        return data;
      }
    }
  } catch (err) {
    console.warn('Gallery sync warning:', err);
  }
  return getStoredGallery();
}

// ==================== SOCIAL MEDIA LINKS ENGINE ====================

const SOCIAL_LINKS_KEY = "aurahair_social_links_v1";

export const DEFAULT_SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/rotterdamaurahair",
  instagram: "https://www.instagram.com/rotterdamaurahair",
  tiktok: "https://www.tiktok.com/@rotterdamaurahair",
  youtube: "https://www.youtube.com/@rotterdamaurahair"
};

export function getStoredSocialLinks() {
  try {
    const data = localStorage.getItem(SOCIAL_LINKS_KEY);
    if (!data) {
      localStorage.setItem(SOCIAL_LINKS_KEY, JSON.stringify(DEFAULT_SOCIAL_LINKS));
      return DEFAULT_SOCIAL_LINKS;
    }
    const parsed = JSON.parse(data);
    return { ...DEFAULT_SOCIAL_LINKS, ...parsed };
  } catch (e) {
    return DEFAULT_SOCIAL_LINKS;
  }
}

export function saveStoredSocialLinks(links) {
  const current = getStoredSocialLinks();
  const updated = { ...current, ...links };
  localStorage.setItem(SOCIAL_LINKS_KEY, JSON.stringify(updated));

  if (typeof fetch !== 'undefined') {
    fetch(`${getApiBaseUrl()}/api/social-links`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updated)
    }).catch(() => {});
  }

  broadcastDataChange('SOCIAL_LINKS_UPDATED', updated);
  return updated;
}

export async function fetchSocialLinksFromServer() {
  if (typeof fetch === 'undefined') return getStoredSocialLinks();
  try {
    const res = await fetch(`${getApiBaseUrl()}/api/social-links`);
    if (res.ok) {
      const data = await res.json();
      if (data && typeof data === 'object') {
        const merged = { ...DEFAULT_SOCIAL_LINKS, ...data };
        localStorage.setItem(SOCIAL_LINKS_KEY, JSON.stringify(merged));
        broadcastDataChange('SOCIAL_LINKS_SYNCED', merged);
        return merged;
      }
    }
  } catch (err) {
    console.warn('Social links sync warning:', err);
  }
  return getStoredSocialLinks();
}

